import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import walletApi from '../../api/wallet';
import { getErrorMessage } from '../../utils/errors';
import {
  CATEGORY_TYPES,
  CATEGORY_TYPE_OPTIONS,
  validateCategoryForm,
} from './categoryUtils';

const INITIAL_FORM_DATA = {
  name: '',
  categoryType: '',
  allowsPositionLink: false,
};

const CategoryForm = ({ open, category, onClose, onSave }) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const isEditMode = Boolean(category?.ID);

  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          name: category.Name || '',
          categoryType: category.CategoryType || '',
          allowsPositionLink: category.AllowsPositionLink || false,
        });
      } else {
        setFormData(INITIAL_FORM_DATA);
      }
      setErrors({});
      setApiError('');
    }
  }, [open, category]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSave = async () => {
    const validationErrors = validateCategoryForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setApiError('');

    const payload = {
      name: formData.name.trim(),
      category_type: formData.categoryType,
      allows_position_link:
        formData.categoryType === CATEGORY_TYPES.CASH_IN
          ? formData.allowsPositionLink
          : false,
    };

    try {
      if (isEditMode) {
        await walletApi.put(`/category/${category.ID}`, payload);
      } else {
        await walletApi.post('/category', payload);
      }
      onSave();
      onClose();
    } catch (err) {
      setApiError(getErrorMessage(err, 'Erro ao salvar a categoria.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Editar Categoria' : 'Criar Categoria'}
      </DialogTitle>
      <DialogContent>
        {apiError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setApiError('')}>
              {apiError}
            </Alert>
          </Box>
        )}

        <TextField
          fullWidth
          label="Nome"
          value={formData.name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          margin="dense"
          required
          error={Boolean(errors.name)}
          helperText={errors.name}
          disabled={submitting}
          autoFocus
        />

        <FormControl
          fullWidth
          margin="dense"
          error={Boolean(errors.categoryType)}
          required
        >
          <InputLabel>Tipo</InputLabel>
          <Select
            value={formData.categoryType}
            onChange={(e) => handleFieldChange('categoryType', e.target.value)}
            disabled={submitting}
            label="Tipo"
          >
            {CATEGORY_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {errors.categoryType && (
            <FormHelperText>{errors.categoryType}</FormHelperText>
          )}
        </FormControl>

        {formData.categoryType === CATEGORY_TYPES.CASH_IN && (
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowsPositionLink}
                onChange={(e) =>
                  handleFieldChange('allowsPositionLink', e.target.checked)
                }
                color="primary"
                disabled={submitting}
              />
            }
            label="Permite vincular a posicao de investimento"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="primary" disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CategoryForm.propTypes = {
  open: PropTypes.bool.isRequired,
  category: PropTypes.shape({
    ID: PropTypes.string,
    Name: PropTypes.string,
    CategoryType: PropTypes.string,
    AllowsPositionLink: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default CategoryForm;
